"use client";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { cn } from "@/lib/utils";
import { useMemo } from "react";
import type { AutocompleteOption, AutocompleteProps } from "./types";

/**
 * A controlled single-select autocomplete input.
 *
 * The input is always visible on the page; the dropdown opens when the user
 * types. Selecting an option fills the input with the option's label.
 *
 * This component is UI-only — it does not fetch data or know about forms.
 * Use `AsyncSingleSelectField` for async data, or wrap with RHF for form use.
 */
function AutocompleteSingleSelect({
  // Core
  options,
  value,
  onValueChange,
  // Display
  placeholder = "Search...",
  emptyMessage = "No options found.",
  // Behaviour
  disabled = false,
  showClear = true,
  autoHighlight = false,
  // Styling
  className,
  // Accessibility
  id,
  name,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-required": ariaRequired,
  "auto-focus": autoFocus,
  onBlur,
}: AutocompleteProps) {
  const { inputClass, contentClass, emptyClass, listClass, itemClass } =
    className ?? {};

  // Build a value → option map for O(1) lookup on every render.
  const optionMap = useMemo(
    () => new Map(options.map((o) => [o.value, o])),
    [options]
  );

  // The Combobox primitive works with full option objects, not string IDs.
  // Convert the consumer's string value to an object before passing it down.
  const selectedOption = value !== null ? (optionMap.get(value) ?? null) : null;

  // Convert back from object → string when the user picks or clears an option.
  // This keeps the consumer API simple: always string | null, never objects.
  const handleValueChange = (selected: AutocompleteOption | null) => {
    onValueChange(selected?.value ?? null);
  };

  return (
    <Combobox<AutocompleteOption>
      items={options}
      // Drives both the input fill text and the built-in filter matching.
      // Filtering is by label (what the user sees), not by value (the ID).
      itemToStringValue={(option) => option.label}
      value={selectedOption}
      onValueChange={handleValueChange}
      autoHighlight={autoHighlight}
      disabled={disabled}
    >
      <ComboboxInput
        id={id}
        name={name}
        placeholder={placeholder}
        showClear={showClear}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        autoFocus={autoFocus}
        onBlur={onBlur}
        className={cn("w-full", inputClass)}
      />

      <ComboboxContent className={cn(contentClass)}>
        <ComboboxEmpty className={cn(emptyClass)}>{emptyMessage}</ComboboxEmpty>

        {/* ComboboxList uses a render-prop — Base UI calls the child function
            once per filtered item. Filtering is handled internally. */}
        <ComboboxList className={cn(listClass)}>
          {(option: AutocompleteOption) => (
            <ComboboxItem
              key={option.value}
              value={option}
              disabled={option.disabled}
              className={cn(option.disabled && "cursor-not-allowed", itemClass)}
            >
              {option.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

export default AutocompleteSingleSelect;
